/**
 * Organization relationships (ADR-0050).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ════════════════════════════════════════════════════════════════════════════
 * `/api/organization` has had complete CRUD since ADR-0050 M3 — list, create,
 * rename, archive, link, unlink — and NOTHING in any frontend called it. The
 * model was built, mounted and unreachable, so no owner could describe their
 * organization and no reviewer could see whether the model was right.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * GROUPING IS NOT ACCESS. THIS SCREEN MUST NOT IMPLY OTHERWISE
 * ════════════════════════════════════════════════════════════════════════════
 * ADR-0050's rule is that structure describes a business and grants nothing.
 * Visibility comes from MEMBERSHIP alone, which is why the API returns
 * `selectable` per node — true only when the node is a workspace the caller
 * actually holds.
 *
 * So this screen never offers a parent organization as somewhere to go, and it
 * says why in words rather than leaving a disabled control to be read as a
 * bug. A UI that let someone "enter" a parent would be teaching the opposite
 * of the rule, and the next reader would implement it.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHAT THE API REFUSES, AND HOW THAT READS HERE
 * ════════════════════════════════════════════════════════════════════════════
 * The service owns every rule; this screen only reports its answers. The two
 * worth naming are `would_cycle` (409 — an organization cannot contain
 * itself, however indirectly) and `workspace_already_mapped` (409 — a
 * workspace belongs to one node). `unknown_node` and `workspace_not_yours`
 * both answer 404 ON PURPOSE, so a refusal cannot be used to confirm that
 * someone else's organization exists; the copy here therefore says "not
 * available" rather than "does not exist".
 */
import { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../../../utils/authFetch';

/** The five kinds the model accepts (`ORG_NODE_KINDS`). */
const NODE_KINDS = ['organization', 'business', 'brand', 'location', 'client'] as const;
type NodeKind = (typeof NODE_KINDS)[number];

/** The four relations the model accepts (`ORG_EDGE_RELATIONS`). */
const RELATIONS = ['owns', 'manages_as_client', 'operates_at', 'brands'] as const;
type Relation = (typeof RELATIONS)[number];

const RELATION_LABEL: Readonly<Record<Relation, string>> = {
  owns: 'owns',
  manages_as_client: 'manages as client',
  operates_at: 'operates at',
  brands: 'brands',
};

interface OrgNode {
  id: string;
  kind: NodeKind;
  name: string;
  operatingWorkspaceId: string | null;
  status: 'active' | 'archived';
  selectable: boolean;
}

interface OrgEdge {
  parentNodeId: string;
  childNodeId: string;
  relation: Relation;
}

/** The service's refusal codes, in words an owner can act on. */
const REFUSAL: Readonly<Record<string, string>> = {
  invalid_kind: 'That is not a kind this model accepts.',
  invalid_name: 'A name is required.',
  invalid_relation: 'That is not a relationship this model accepts.',
  would_cycle: 'That would make an organization contain itself. Relationships have to form a tree.',
  workspace_already_mapped: 'That workspace is already part of another node.',
  // 404 covers "no such node" AND "not yours", deliberately indistinguishable.
  unknown_node: 'That item is not available.',
  workspace_not_yours: 'That item is not available.',
  AUTH_REQUIRED: 'Your session has expired. Sign in again.',
};

function messageFor(code: unknown, fallback: string): string {
  return (typeof code === 'string' && REFUSAL[code]) || fallback;
}

interface OrganizationTabProps {
  authToken: string;
  /** Only to mark which node is the workspace being viewed; never an authority. */
  workspaceId?: string | undefined;
}

export function OrganizationTab({ authToken, workspaceId }: OrganizationTabProps) {
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [edges, setEdges] = useState<OrgEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form
  const [newKind, setNewKind] = useState<NodeKind>('organization');
  const [newName, setNewName] = useState('');
  /**
   * Whether the new node IS the workspace being viewed.
   *
   * `operatingWorkspaceId` is what turns a described thing into the workspace
   * people actually work in, and the API has accepted it since M3 — but no
   * screen ever sent it, so the binding was unreachable and every node was
   * description only. See the header note on which workspace can be bound.
   */
  const [bindCurrentWorkspace, setBindCurrentWorkspace] = useState(false);

  /** The node being renamed in place, and the text so far. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  /** The node whose Archive is awaiting a second click. */
  const [confirmingArchiveId, setConfirmingArchiveId] = useState<string | null>(null);

  // Link form
  const [parentId, setParentId] = useState('');
  const [childId, setChildId] = useState('');
  const [relation, setRelation] = useState<Relation>('owns');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/organization', authToken);
      const body = (await res.json()) as {
        ok?: boolean;
        nodes?: OrgNode[];
        edges?: OrgEdge[];
        code?: string;
      };
      if (!res.ok || body.ok !== true) {
        setError(messageFor(body.code, 'Could not load your organization.'));
        return;
      }
      setNodes(body.nodes ?? []);
      setEdges(body.edges ?? []);
    } catch {
      // A transport failure is NOT "you have no organization" — saying so
      // would invite someone to rebuild a structure that already exists.
      setError('Could not reach Central. Your organization has not been changed.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every mutation goes through here: one place that re-reads on success. */
  const mutate = useCallback(
    async (path: string, init: RequestInit, fallback: string): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        const res = await authFetch(`/api/organization${path}`, authToken, {
          ...init,
          headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
        });
        let code: unknown;
        try {
          code = ((await res.json()) as { code?: unknown }).code;
        } catch {
          code = undefined;
        }
        if (!res.ok) {
          setError(messageFor(code, fallback));
          return false;
        }
        // RE-READ rather than patching local state: the service decides what
        // the graph is, including what a change did to `selectable`.
        await load();
        return true;
      } catch {
        setError('Could not reach Central. Nothing was changed.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [authToken, load],
  );

  const createNode = async () => {
    const name = newName.trim();
    if (!name) {
      setError(REFUSAL.invalid_name ?? 'A name is required.');
      return;
    }
    // THE BINDING IS SENT ONLY WHEN ASKED FOR, and only ever as the workspace
    // being viewed. The service refuses a workspace the caller does not hold
    // (`workspace_not_yours`) and one already mapped
    // (`workspace_already_mapped`); this never tries to pre-empt either, so
    // the answer stays the service's.
    const ok = await mutate(
      '/nodes',
      {
        method: 'POST',
        body: JSON.stringify({
          kind: newKind,
          name,
          ...(bindCurrentWorkspace && workspaceId !== undefined
            ? { operatingWorkspaceId: workspaceId }
            : {}),
        }),
      },
      'Could not create that.',
    );
    if (ok) {
      setNewName('');
      setBindCurrentWorkspace(false);
    }
  };

  const startRename = (node: OrgNode) => {
    // Opening one editor closes any pending archive, so the row never shows
    // two different pending decisions at once.
    setConfirmingArchiveId(null);
    setEditingId(node.id);
    setEditingName(node.name);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  /**
   * RENAMED IN PLACE, not through `window.prompt`.
   *
   * `prompt()` was the first implementation and had to go: it is blocked
   * outright in a cross-origin iframe, suppressed by browsers after repeated
   * dialogs, unstyled, not reachable by assistive technology in the way a
   * labelled input is, and — the reason that matters most here — it cannot
   * show this screen's refusal copy, so a `would_cycle`-style answer would
   * have been reported into a dialog that had already closed.
   */
  const commitRename = async (node: OrgNode) => {
    const name = editingName.trim();
    if (!name) {
      setError(REFUSAL.invalid_name ?? 'A name is required.');
      return;
    }
    // An unchanged name is not a request. Closing the editor is the honest
    // response; a PATCH would write an audit entry for nothing.
    if (name === node.name) {
      cancelRename();
      return;
    }
    const ok = await mutate(
      `/nodes/${encodeURIComponent(node.id)}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
      'Could not rename that.',
    );
    // ONLY ON SUCCESS. A failed rename keeps the editor open with what was
    // typed, next to the reason — closing it would discard the input and
    // leave the owner with an error about a field they can no longer see.
    if (ok) cancelRename();
  };

  /**
   * ARCHIVE, never delete. The API has no delete at all: a node may be the
   * only record that a location existed, and historical references to it must
   * keep resolving.
   *
   * Confirmed IN THE ROW rather than with `window.confirm`, for the same
   * reasons as the rename, plus one of its own: the confirmation has to say
   * what archiving does and what it does not do, and a native dialog that an
   * owner has learned to dismiss is not a decision point.
   *
   * NOTE FOR THE OWNER: there is no un-archive endpoint, so this is one-way
   * from here. The copy says so rather than implying a reversible action.
   */
  const archive = async (node: OrgNode) => {
    const ok = await mutate(
      `/nodes/${encodeURIComponent(node.id)}/archive`,
      { method: 'POST' },
      'Could not archive that.',
    );
    if (ok) setConfirmingArchiveId(null);
  };

  /**
   * The inverse of archive, and the reason Archive's copy no longer has to
   * say it is permanent.
   *
   * Not confirmed: restoring is not destructive and is itself reversible, so
   * a confirmation here would be ceremony. Archive keeps its confirmation
   * because archiving removes something from the current structure.
   */
  const unarchive = async (node: OrgNode) => {
    await mutate(
      `/nodes/${encodeURIComponent(node.id)}/unarchive`,
      { method: 'POST' },
      'Could not restore that.',
    );
  };

  const link = async () => {
    if (!parentId || !childId) {
      setError('Choose both a parent and a child.');
      return;
    }
    const ok = await mutate(
      '/edges',
      {
        method: 'POST',
        body: JSON.stringify({ parentNodeId: parentId, childNodeId: childId, relation }),
      },
      'Could not relate those.',
    );
    if (ok) {
      setParentId('');
      setChildId('');
    }
  };

  const unlink = async (e: OrgEdge) => {
    await mutate(
      '/edges',
      {
        method: 'DELETE',
        body: JSON.stringify({
          parentNodeId: e.parentNodeId,
          childNodeId: e.childNodeId,
          relation: e.relation,
        }),
      },
      'Could not remove that relationship.',
    );
  };

  const nameOf = (id: string): string => nodes.find((n) => n.id === id)?.name ?? 'Not available';
  const active = nodes.filter((n) => n.status === 'active');
  const archived = nodes.filter((n) => n.status === 'archived');

  /**
   * The node already holding the workspace being viewed, if any.
   *
   * Read across ALL nodes, not just active ones: the service's
   * `workspace_already_mapped` check does not care about status, so an
   * archived node still holds the workspace and offering to bind it again
   * would produce a refusal the owner cannot act on.
   */
  const nodeHoldingThisWorkspace =
    workspaceId === undefined
      ? undefined
      : nodes.find((n) => n.operatingWorkspaceId === workspaceId);
  const canBindThisWorkspace = workspaceId !== undefined && nodeHoldingThisWorkspace === undefined;

  if (loading) {
    return (
      <div className="p-6" data-testid="organization-loading">
        <p className="text-sm text-gray-500">Loading your organization…</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8" data-testid="organization-tab">
      <header>
        <h2 className="text-lg font-semibold text-gray-900">Organization</h2>
        <p className="mt-1 text-sm text-gray-600">
          Describe how your businesses, brands, locations and clients relate.
        </p>
        {/* THE RULE, stated. See this file's header for why it is copy and not
            just a disabled button. */}
        <p className="mt-2 text-sm text-gray-500" data-testid="organization-access-note">
          Structure here is description only — it does not give anyone access. People reach a
          workspace through membership, so adding something to your organization never grants a
          permission.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          data-testid="organization-error"
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {/* ── Current structure ───────────────────────────────────────────── */}
      <section aria-labelledby="org-structure-heading">
        <h3 id="org-structure-heading" className="text-sm font-semibold text-gray-900">
          Current structure
        </h3>
        {active.length === 0 && error === null ? (
          // GATED ON `error === null`. Without that this rendered "Nothing
          // here yet" NEXT TO a transport failure, which invites an owner to
          // rebuild a structure that already exists and that we merely failed
          // to read. An unknown graph is not an empty one. Caught by OT11.
          <p className="mt-2 text-sm text-gray-500" data-testid="organization-empty">
            Nothing here yet. Add your business, a brand, a location or a client below.
          </p>
        ) : active.length === 0 ? null : (
          <ul className="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200">
            {active.map((n) => (
              <li
                key={n.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
                data-testid={`organization-node-${n.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{n.name}</p>
                  <p className="text-xs text-gray-500">
                    {n.kind}
                    {n.operatingWorkspaceId !== null && (
                      <>
                        {' · '}
                        {n.selectable ? (
                          <span data-testid={`organization-node-${n.id}-yours`}>
                            a workspace you belong to
                          </span>
                        ) : (
                          // NOT "no access" — it is context the owner described.
                          <span data-testid={`organization-node-${n.id}-context`}>
                            a workspace you are not a member of
                          </span>
                        )}
                      </>
                    )}
                    {n.operatingWorkspaceId === workspaceId && workspaceId !== undefined && (
                      <> · currently viewing</>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {editingId === n.id ? (
                    <>
                      <input
                        aria-label={`New name for ${n.name}`}
                        data-testid={`organization-rename-input-${n.id}`}
                        value={editingName}
                        onChange={(ev) => setEditingName(ev.target.value)}
                        onKeyDown={(ev) => {
                          // Enter saves and Escape cancels, because an input
                          // that only responds to a mouse is a worse dialog
                          // than the one this replaced.
                          if (ev.key === 'Enter') void commitRename(n);
                          if (ev.key === 'Escape') cancelRename();
                        }}
                        className="w-40 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      <button
                        type="button"
                        data-testid={`organization-rename-save-${n.id}`}
                        onClick={() => void commitRename(n)}
                        disabled={busy}
                        className="rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        data-testid={`organization-rename-cancel-${n.id}`}
                        onClick={cancelRename}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                      >
                        Cancel
                      </button>
                    </>
                  ) : confirmingArchiveId === n.id ? (
                    <>
                      {/* What it does AND what it does not do. */}
                      <span
                        data-testid={`organization-archive-confirm-note-${n.id}`}
                        className="text-xs text-gray-600"
                      >
                        Archive “{n.name}”? It stops appearing as a current part of your
                        organization. Relationships and history are kept, and you can restore it
                        from Archived below.
                      </span>
                      <button
                        type="button"
                        data-testid={`organization-archive-confirm-${n.id}`}
                        onClick={() => void archive(n)}
                        disabled={busy}
                        className="rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                      >
                        Archive
                      </button>
                      <button
                        type="button"
                        data-testid={`organization-archive-cancel-${n.id}`}
                        onClick={() => setConfirmingArchiveId(null)}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                      >
                        Keep
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        data-testid={`organization-rename-${n.id}`}
                        onClick={() => startRename(n)}
                        disabled={busy}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        data-testid={`organization-archive-${n.id}`}
                        onClick={() => {
                          setEditingId(null);
                          setConfirmingArchiveId(n.id);
                        }}
                        disabled={busy}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                      >
                        Archive
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Relationships ──────────────────────────────────────────────── */}
      <section aria-labelledby="org-relationships-heading">
        <h3 id="org-relationships-heading" className="text-sm font-semibold text-gray-900">
          Relationships
        </h3>
        {edges.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500" data-testid="organization-no-edges">
            No relationships yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {edges.map((e) => (
              <li
                key={`${e.parentNodeId}-${e.relation}-${e.childNodeId}`}
                className="flex items-center justify-between gap-3 text-sm"
                data-testid={`organization-edge-${e.parentNodeId}-${e.childNodeId}`}
              >
                <span className="text-gray-700">
                  {nameOf(e.parentNodeId)}{' '}
                  <span className="text-gray-400">{RELATION_LABEL[e.relation]}</span>{' '}
                  {nameOf(e.childNodeId)}
                </span>
                <button
                  type="button"
                  onClick={() => void unlink(e)}
                  disabled={busy}
                  className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {active.length >= 2 && (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">
              Parent
              <select
                aria-label="Parent"
                value={parentId}
                onChange={(ev) => setParentId(ev.target.value)}
                className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="">Choose…</option>
                {active.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Relationship
              <select
                aria-label="Relationship"
                value={relation}
                onChange={(ev) => setRelation(ev.target.value as Relation)}
                className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm"
              >
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {RELATION_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Child
              <select
                aria-label="Child"
                value={childId}
                onChange={(ev) => setChildId(ev.target.value)}
                className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="">Choose…</option>
                {active.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void link()}
              disabled={busy}
              className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              Relate
            </button>
          </div>
        )}
      </section>

      {/* ── Add ─────────────────────────────────────────────────────────── */}
      <section aria-labelledby="org-add-heading">
        <h3 id="org-add-heading" className="text-sm font-semibold text-gray-900">
          Add to your organization
        </h3>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-600">
            Kind
            <select
              aria-label="Kind"
              value={newKind}
              onChange={(ev) => setNewKind(ev.target.value as NodeKind)}
              className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm"
            >
              {NODE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            Name
            <input
              aria-label="Name"
              value={newName}
              onChange={(ev) => setNewName(ev.target.value)}
              className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => void createNode()}
            disabled={busy}
            className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            Add
          </button>
        </div>

        {/* ── Workspace link ──────────────────────────────────────────────
            The one thing that makes a node more than a description: binding
            it to the workspace people actually work in. Offered only for the
            workspace being VIEWED, because holding a workspace is what
            authorizes the binding and that is the one we can be sure of —
            the service refuses anything else as `workspace_not_yours`. */}
        {canBindThisWorkspace ? (
          <label
            className="mt-3 flex items-start gap-2 text-xs text-gray-600"
            data-testid="organization-bind-workspace"
          >
            <input
              type="checkbox"
              aria-label="This is the workspace I am in now"
              checked={bindCurrentWorkspace}
              onChange={(ev) => setBindCurrentWorkspace(ev.target.checked)}
              className="mt-0.5"
            />
            <span>
              This is the workspace I’m in now. Linking it records which part of your
              organization this workspace is — it still grants nothing, and people continue to
              reach it through membership.
            </span>
          </label>
        ) : nodeHoldingThisWorkspace !== undefined ? (
          <p className="mt-3 text-xs text-gray-500" data-testid="organization-bind-workspace-taken">
            The workspace you’re in is already linked to “{nodeHoldingThisWorkspace.name}”. A
            workspace belongs to one part of your organization.
          </p>
        ) : null}

        {/* Said once, so the absence of a workspace picker reads as a rule
            rather than a missing feature. */}
        <p className="mt-2 text-xs text-gray-500" data-testid="organization-bind-other-note">
          To link a different workspace, open Settings while you’re in that workspace.
        </p>
      </section>

      {archived.length > 0 && (
        <section aria-labelledby="org-archived-heading">
          <h3 id="org-archived-heading" className="text-sm font-semibold text-gray-900">
            Archived
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-gray-500">
            {archived.map((n) => (
              <li
                key={n.id}
                data-testid={`organization-archived-${n.id}`}
                className="flex items-center justify-between gap-3"
              >
                <span>
                  {n.name} · {n.kind}
                </span>
                <button
                  type="button"
                  data-testid={`organization-restore-${n.id}`}
                  onClick={() => void unarchive(n)}
                  disabled={busy}
                  className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
